import { Display } from "rot-js"
import { MapID } from "../defines/enums"
import { MapData, Offset } from "../defines/types"
import { Dims } from "../defines/values"
import { Player } from "../player/player"
import { generateRandomMap, mapIDToString } from "../utils/map"
import { Room } from "./room"
import { TapestryMap } from "./tapestrymap"
import { Tile } from "./tile"

export class DynamicMap extends TapestryMap {
  constructor(
    mapData: MapData,
    mapID: MapID,
    display: Display,
    player: Player,
    offsets: Offset
  ) {
    const viewportWidth = mapData.viewportWidth
      ? mapData.viewportWidth
      : Dims.DEFAULT_VIEWPORT_WIDTH
    const viewportHeight = mapData.viewportHeight
      ? mapData.viewportHeight
      : Dims.DEFAULT_VIEWPORT_HEIGHT

    super(
      mapID,
      display,
      player,
      mapData.width,
      mapData.height,
      viewportWidth,
      viewportHeight,
      mapData.enableFogOfWar
    )

    // 1. Build rooms
    let rooms = this.buildRooms(offsets, mapData.randomOptions)

    // 2. Add maze room(s)
    if (mapData.mazeRooms !== undefined) {
      const mazeRoomIndexes = this.addMazeRooms(mapData.mazeRooms, rooms)
      // exclude maze rooms for rest of build
      rooms = rooms.filter((_, idx) => mazeRoomIndexes.includes(idx) == false)
    }

    // 3. Place portals
    let startPoint = this.placePortals(mapData.portals, rooms)

    // 4. Place the player (+1 to move right of the portal)
    startPoint.x += 1
    this.player.setPosition(startPoint)

    // 5. Place pigments
    if (mapData.palette !== undefined) {
      this.placePigments(mapData.palette, rooms)
    }

    // 6. Place items
    if (mapData.items !== undefined) {
      this.placeItems(mapData.items, rooms)
    }

    // 7. Place binders
    if (mapData.binder !== undefined) {
      const diff =
        mapData.binder.maxEver - this.player.getBindersCollected(this.mapID)
      if (diff > 0) {
        this.placeBinder(mapData.binder, diff, rooms)
      } else {
        window.engine.sendMessage(
          "You have found all of the binders for " + mapIDToString(this.mapID)
        )
      }
    }

    // 8. Place equipment
    if (mapData.equipment !== undefined) {
      this.placeEquipment(mapData.equipment, rooms)
    }

    // 9. Place enemies
    if (mapData.enemies !== undefined) {
      const result = this.placeEnemies(mapData.enemies, rooms)
    }
  }

  private buildRooms(offsets: Offset, options: any): Room[] {
    let rooms: Room[]
    let tiles: Tile[][]
    ;[rooms, tiles] = generateRandomMap(
      this.width - offsets.xOffset * 2,
      this.height - offsets.yOffset * 2,
      options.roomWidthRange,
      options.roomHeightRange,
      options.maxFillPercentage,
      options.timeLimit
    )

    window.engine.displayRandomRoomsGenerated(rooms.length)

    // fix tiles for offets
    for (let y = 0; y < tiles.length; ++y) {
      for (let x = 0; x < tiles[y].length; ++x) {
        this._tiles[y + offsets.yOffset][x + offsets.xOffset] = tiles[y][x]
      }
    }

    // fix rooms, accounting for offets
    rooms.forEach((room) => {
      room.x += offsets.xOffset
      room.y += offsets.yOffset
    })

    return rooms
  }
}
